import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Settings2 } from 'lucide-react';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';
import { QuestionnairePicker } from '@/components/admin/QuestionnairePicker';
import { suiteDisplayName } from '@/lib/i18nTitles';
import { LocalizedDateInput } from '@/components/shared/LocalizedDateInput';
import { toast } from 'sonner';

// UAT 07/18 — Evaluation Wizard (Sprint Preparo Phase 1, frontend only).
// Configures a DRAFT assignment created by an evaluation-request approval
// through the EXISTING PATCH /admin/assignments/{id}. The backend remains
// the source of truth for all validation.
// Sprint Preparo Phase 2 — questionnaire <Select> replaced by the scalable
// QuestionnairePicker; summary fields sourced from the Canonical Employee
// Resolver (falls back to the embedded assignment payload).
export function ConfigureAssignmentDialog({ open, onOpenChange, assignment, onConfigured }) {
  const { t, i18n } = useTranslation();
  const [resolved, setResolved] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deadline, setDeadline] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !assignment) return;
    setSelected(null); setDeadline(''); setResolved(null);
    const uid = assignment.assigned_to || assignment.candidate?.id;
    if (uid) {
      adminApi.resolveEmployees([uid])
        .then((rs) => setResolved(rs?.[0] || null))
        .catch(() => setResolved(null));
    }
  }, [open, assignment]);

  const onConfirm = async () => {
    if (!selected || !deadline) {
      toast.error(t('configureAssignment.missing', { defaultValue: 'Select a questionnaire and a deadline.' }));
      return;
    }
    setBusy(true);
    try {
      const iso = new Date(`${deadline}T23:59:59`).toISOString();
      const res = await adminApi.patchAssignment(assignment.id, {
        questionnaire_id: selected.id,
        questionnaire_version_id: selected.current_version_id,
        deadline: iso,
        status: 'requested',
      });
      const st = res?.assignment?.status || res?.status || 'requested';
      toast.success(t('configureAssignment.success', { defaultValue: 'Evaluation configured' }), {
        description: t('configureAssignment.successDesc', {
          status: st,
          defaultValue: 'Assignment status is now: {{status}}',
        }),
      });
      onOpenChange(false);
      onConfigured?.();
    } catch (e) {
      // Display the real backend validation message to the advisor.
      toast.error(t('configureAssignment.failed', { defaultValue: 'Configuration rejected' }), {
        description: formatApiError(e),
      });
    } finally { setBusy(false); }
  };

  if (!assignment) return null;
  const isES = (i18n.language || '').toLowerCase().startsWith('es');
  const dept = resolved?.department
    ? (isES ? resolved.department.name_es : resolved.department.name_en) || resolved.department.name
    : null;
  const rows = [
    [t('configureAssignment.client', { defaultValue: 'Client' }), resolved?.company?.name || assignment.company?.name || '—'],
    [t('configureAssignment.collaborator', { defaultValue: 'Collaborator' }), resolved?.full_name || assignment.candidate?.name || '—'],
    [t('configureAssignment.candidateEmail', { defaultValue: 'Candidate email' }), assignment.candidate?.email || resolved?.email || '—'],
    // TC-UAT — the evaluation context: position APPLIED FOR (from the
    // request) is distinct from the person's CURRENT job title.
    [t('configureAssignment.appliedPosition', { defaultValue: 'Position applied for' }), assignment.position || '—'],
    [t('configureAssignment.position', { defaultValue: 'Current job title' }), resolved?.position || assignment.candidate?.position || '—'],
    [t('configureAssignment.department', { defaultValue: 'Department' }), dept || '—'],
    [t('configureAssignment.suite', { defaultValue: 'Requested suite' }), suiteDisplayName(assignment.suite_code)],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="configure-assignment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings2 className="h-4 w-4" />
            {t('configureAssignment.title', { defaultValue: 'Configure evaluation' })}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-muted/30 p-3 grid grid-cols-2 gap-x-4 gap-y-1.5" data-testid="configure-assignment-summary">
            {rows.map(([k, v]) => (
              <React.Fragment key={k}>
                <span className="text-xs text-muted-foreground">{k}</span>
                <span className={`text-xs font-medium ${String(v).includes('@') ? 'lowercase' : 'capitalize'}`}>{v}</span>
              </React.Fragment>
            ))}
          </div>
          <div>
            <Label className="text-xs">{t('configureAssignment.questionnaire', { defaultValue: 'Questionnaire (published, suite-compatible)' })}</Label>
            <QuestionnairePicker
              suiteCode={assignment.suite_code}
              value={selected}
              onSelect={setSelected}
            />
          </div>
          <div>
            <Label className="text-xs">{t('configureAssignment.deadline', { defaultValue: 'Deadline' })}</Label>
            <LocalizedDateInput
              value={deadline}
              min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
              onChange={setDeadline}
              data-testid="configure-deadline-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="configure-cancel-btn">
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button onClick={onConfirm} disabled={busy || !selected || !deadline}
                  className="bg-gradient-primary hover:opacity-90 gap-1.5"
                  data-testid="configure-confirm-btn">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('configureAssignment.confirm', { defaultValue: 'Confirm configuration' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ConfigureAssignmentDialog;
